import { useMemo } from 'react';
import { notification } from 'antd';
import { I18n } from '@aws-amplify/core';

import type { CycleCallback } from '@/components/ui/Timeline';
import { toSegmentsPayload } from '@/components/views/BeraJob/BeraReportV2/utils';
import type { BeraReportV2 } from '@/types/Bera';
import type { UpdateAllBeraReportSegmentsReport } from '@/components/views/BeraJob/hooks/types/request';
import {
	useCreateBeraReportV2,
	useDeleteBeraReportV2,
	useUpdateAllBeraReportSegments,
	useUpdateBeraReportSegments
} from '@/components/views/BeraJob/hooks/useBeraReportV2';

import { findStretchIdsPastVideo, toTaskIds } from '@/components/views/BeraJob/BeraReportV2/Timeline/helpers';

export const SAVE_RESULT = {
	SAVED: 'saved',
	FAILED: 'failed',
	NOTHING_TO_SAVE: 'nothing-to-save'
} as const;

export type SaveResult = (typeof SAVE_RESULT)[keyof typeof SAVE_RESULT];

export interface ConfirmStretchesParams {
	taskId: string;
	stretches: CycleCallback[];
	onSuccess: () => void;
	reportId?: string;
}

export interface RemoveStretchesParams {
	reportId: string;
	onSuccess: () => void;
}

export interface RulerEditsParams {
	rulerEdits: Record<string, CycleCallback>;
	rulerStretches: CycleCallback[];
}

interface UseStretchPersistenceParams {
	organizationId: string;
	companyId: string;
	beraJobSummaryId: string;
	reports: BeraReportV2[];
	videoDuration: number;
}

function findStretchesOfTask(stretches: CycleCallback[], taskId: string): CycleCallback[] {
	const result = stretches.filter((stretch) => stretch.type_id === taskId);

	return result;
}

export function useStretchPersistence({
	organizationId,
	companyId,
	beraJobSummaryId,
	reports,
	videoDuration
}: UseStretchPersistenceParams) {
	const { mutate: createBeraReport, isLoading: creating } = useCreateBeraReportV2();
	const { mutate: updateSegments, isLoading: updating } = useUpdateBeraReportSegments();
	const { mutateAsync: updateAllSegmentsAsync, isLoading: updatingAll } = useUpdateAllBeraReportSegments();
	const { mutate: deleteBeraReport, isLoading: deleting } = useDeleteBeraReportV2();

	const isSaving = creating || updating || updatingAll || deleting;

	const baseParams = { organization_id: organizationId, company_id: companyId };

	const reportIdByTaskId = useMemo(() => {
		const entries = reports.map((report) => [report.task_id, report.id] as const);
		const result = Object.fromEntries(entries);

		return result;
	}, [reports]);

	function notifySaveError(): void {
		notification.error({
			message: I18n.get('Ops... something happened!'),
			description: I18n.get('Could not save the stretches')
		});
	}

	function notifyStretchesPastVideo(): void {
		notification.warning({
			message: I18n.get('Stretches outside the video'),
			description: I18n.get('Every stretch must be inside the current video length to be saved')
		});
	}

	function warnIfStretchesPastVideo(stretches: CycleCallback[]): boolean {
		if (!videoDuration) {
			return false;
		}

		const pastVideoIds = findStretchIdsPastVideo({ stretches, videoDuration });
		const hasPastVideo = !!pastVideoIds.length;

		if (hasPastVideo) {
			notifyStretchesPastVideo();
		}

		return hasPastVideo;
	}

	function confirm({ taskId, stretches, onSuccess, reportId }: ConfirmStretchesParams): void {
		if (warnIfStretchesPastVideo(stretches)) {
			return;
		}

		const segments = toSegmentsPayload(stretches);
		const targetReportId = reportId ?? reportIdByTaskId[taskId];

		if (targetReportId) {
			const payload = { ...baseParams, bera_report_id: targetReportId, task_id: taskId, segments };

			updateSegments(payload, { onSuccess, onError: notifySaveError });
			return;
		}

		const payload = { ...baseParams, bera_job_summary_id: beraJobSummaryId, task_id: taskId, segments };

		createBeraReport(payload, { onSuccess, onError: notifySaveError });
	}

	function remove({ reportId, onSuccess }: RemoveStretchesParams): void {
		const params = { ...baseParams, bera_report_id: reportId };

		deleteBeraReport(params, { onSuccess, onError: notifySaveError });
	}

	function findEditedStretches({ rulerEdits, rulerStretches }: RulerEditsParams): CycleCallback[] {
		const editValues = Object.values(rulerEdits);
		const editedTaskIds = toTaskIds(editValues);
		const result = editedTaskIds.flatMap((taskId) => findStretchesOfTask(rulerStretches, taskId));

		return result;
	}

	function buildSegmentUpdates({
		rulerEdits,
		rulerStretches
	}: RulerEditsParams): UpdateAllBeraReportSegmentsReport[] {
		const editedStretches = Object.values(rulerEdits);
		const editedTaskIds = toTaskIds(editedStretches);

		const updates = editedTaskIds.map((taskId) => {
			const fromTask = findStretchesOfTask(rulerStretches, taskId);
			const report = {
				bera_report_id: reportIdByTaskId[taskId],
				task_id: taskId,
				segments: toSegmentsPayload(fromTask)
			};

			return report;
		});

		const result = updates.filter((update) => !!update.bera_report_id);

		return result;
	}

	async function saveRulerEdits(params: RulerEditsParams): Promise<SaveResult> {
		const reports = buildSegmentUpdates(params);

		if (!reports.length) {
			return SAVE_RESULT.NOTHING_TO_SAVE;
		}

		const editedStretches = findEditedStretches(params);

		if (warnIfStretchesPastVideo(editedStretches)) {
			return SAVE_RESULT.FAILED;
		}

		const payload = { ...baseParams, bera_job_summary_id: beraJobSummaryId, reports };

		try {
			await updateAllSegmentsAsync(payload);
		} catch {
			notifySaveError();

			return SAVE_RESULT.FAILED;
		}

		return SAVE_RESULT.SAVED;
	}

	return { isSaving, confirm, remove, saveRulerEdits };
}
